"use client"

import { useAutoleadsConfig } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import { isValidPhoneNumber } from "libphonenumber-js"
import { Loader2, Plus, X } from "lucide-react"
import { type ReactNode, useEffect, useId, useRef, useState } from "react"

import {
  useAddLeadContact,
  useUpdateLeadContact,
} from "@/lib/apps/autoleads/hooks/use-lead-contact-mutations"
import type { LeadContactPayload } from "@/lib/apps/autoleads/hooks/use-lead-contact-mutations"
import { isHttpUrl } from "@/lib/safe-url"

import PhoneNumberInput, {
  formatPhoneNumberForDisplay,
} from "@/components/nango/common/components/phone-number-input"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import { LEADS_PANE_T } from "./translations"
import type { LeadPerson } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

// Just enough of a contact to fill the form. Narrower than `LeadPerson` on
// purpose: callers that list contacts compactly only carry these fields.
export type ContactFormPerson = Pick<
  LeadPerson,
  "index" | "name" | "role" | "emails" | "phones" | "linkedin_url"
>

// Mirrors the backend limits (LEAD_CONTACT_* in apps/autoleads/constants.py),
// so the form refuses what the API would refuse anyway.
const NAME_MAX_LENGTH = 200
const ROLE_MAX_LENGTH = 200
const LINKEDIN_MAX_LENGTH = 300
const VALUES_MAX = 5
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type TrKey = keyof typeof LEADS_PANE_T & string
// "phone" opens on the number: adding a line to the lead's dialling list,
// where the name and role only label it.
export type ContactDialogIntent = "contact" | "phone"

type Row = { id: number; value: string }

type FormErrors = {
  form?: TrKey
  linkedin?: TrKey
  emails: Record<number, TrKey>
  phones: Record<number, TrKey>
}

const NO_ERRORS: FormErrors = { emails: {}, phones: {} }

// A stored number the shared input can show: E.164 in one of its countries.
// Anything else (a national capture, an unlisted country) would render as an
// empty field there, so it is edited as plain text instead.
function fitsPhoneInput(value: string): boolean {
  return !value || formatPhoneNumberForDisplay(value) !== value
}

function phoneError(value: string): TrKey | undefined {
  if (!value.startsWith("+")) return undefined
  return isValidPhoneNumber(value) ? undefined : "contactErrorPhone"
}

// Values already stored on the entry are not re-checked: a scraped value the
// rules here would refuse must not block saving an unrelated field.
function rowErrors(
  rows: Row[],
  stored: readonly string[],
  check: (value: string) => TrKey | undefined,
): Record<number, TrKey> {
  return Object.fromEntries(
    rows
      .map((row) => row.value.trim())
      .map((value, index) => [
        rows[index].id,
        value && !stored.includes(value) && check(value),
      ])
      .filter(([, error]) => Boolean(error)),
  )
}

function filledValues(rows: Row[]): string[] {
  return rows.map((row) => row.value.trim()).filter(Boolean)
}

function validate(
  form: {
    name: string
    emails: Row[]
    phones: Row[]
    linkedin: string
  },
  person?: ContactFormPerson,
): FormErrors {
  const emails = rowErrors(form.emails, person?.emails ?? [], (value) =>
    EMAIL_PATTERN.test(value) ? undefined : "contactErrorEmail",
  )
  const phones = rowErrors(form.phones, person?.phones ?? [], phoneError)
  const linkedin =
    form.linkedin.trim() && !isHttpUrl(form.linkedin.trim())
      ? "contactErrorLinkedin"
      : undefined
  const isEmpty =
    !form.name.trim() &&
    filledValues(form.emails).length === 0 &&
    filledValues(form.phones).length === 0
  return {
    form: isEmpty ? "contactErrorEmpty" : undefined,
    linkedin,
    emails,
    phones,
  }
}

function hasErrors(errors: FormErrors): boolean {
  return Boolean(
    errors.form ||
    errors.linkedin ||
    Object.keys(errors.emails).length ||
    Object.keys(errors.phones).length,
  )
}

// One repeatable list (emails, numbers): a row per value, a remove button per
// row, an add button under the list. A new row takes the focus so the list can
// be filled from the keyboard alone.
function ValueList({
  legend,
  rows,
  errors,
  addLabel,
  autoFocusFirst = false,
  reserveSlot = false,
  onChange,
  renderInput,
}: {
  legend: string
  rows: Row[]
  errors: Record<number, TrKey>
  addLabel: string
  // Opens with the first row focused, when the list is what the form is for.
  autoFocusFirst?: boolean
  // Keeps the remove column even on a single row, so this list's inputs line
  // up with a sibling list that shows remove buttons.
  reserveSlot?: boolean
  onChange: (rows: Row[]) => void
  renderInput: (
    row: Row,
    inputId: string,
    setValue: (value: string) => void,
  ) => ReactNode
}) {
  const tr = useTr(LEADS_PANE_T)
  const baseId = useId()
  const nextId = useRef(rows.length)
  const [focusId, setFocusId] = useState<number | null>(
    autoFocusFirst ? (rows[0]?.id ?? null) : null,
  )
  useEffect(() => {
    if (focusId == null) return
    document.getElementById(`${baseId}-${focusId}`)?.focus()
  }, [focusId, baseId])

  const setValue = (id: number, value: string) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, value } : row)))
  const add = () => {
    nextId.current += 1
    const id = nextId.current
    onChange([...rows, { id, value: "" }])
    setFocusId(id)
  }
  // A lone row is emptied rather than removed, so the list never vanishes.
  const canRemove = rows.length > 1
  const remove = (id: number) => {
    const index = rows.findIndex((row) => row.id === id)
    const remaining = rows.filter((row) => row.id !== id)
    onChange(remaining)
    // Focus lands on the row above (the new first row when the first goes),
    // never on the page behind the dialog.
    const target = remaining[Math.max(index - 1, 0)]
    if (target) {
      requestAnimationFrame(() =>
        document.getElementById(`${baseId}-${target.id}`)?.focus(),
      )
    }
  }

  return (
    <FieldSet className="gap-2">
      <FieldLegend variant="label" className="mb-1">
        {legend}
      </FieldLegend>
      {rows.map((row) => (
        <Field
          key={row.id}
          data-invalid={Boolean(errors[row.id])}
          className="gap-1.5"
        >
          <div className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              {renderInput(row, `${baseId}-${row.id}`, (value) =>
                setValue(row.id, value),
              )}
            </div>
            {canRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                tone="muted"
                aria-label={tr("contactRemoveValue")}
                title={tr("contactRemoveValue")}
                onClick={() => remove(row.id)}
              >
                <X />
              </Button>
            ) : (
              reserveSlot && <span aria-hidden className="size-9 shrink-0" />
            )}
          </div>
          {errors[row.id] && <FieldError>{tr(errors[row.id])}</FieldError>}
        </Field>
      ))}
      {rows.length < VALUES_MAX && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          tone="muted"
          className="-ml-2.5 self-start"
          onClick={add}
        >
          <Plus />
          {addLabel}
        </Button>
      )}
    </FieldSet>
  )
}

function toRows(values: string[] | undefined, withBlank: boolean): Row[] {
  const rows = (values ?? []).map((value, index) => ({ id: index, value }))
  return rows.length === 0 && withBlank ? [{ id: 0, value: "" }] : rows
}

// Add / edit form for one contact of a lead, or a new number for its dialling
// list. Mounted only while open (the caller keys it on the edited contact) so
// the fields always start on the person being worked, never on the previous
// one.
export function LeadContactDialog({
  leadId,
  person,
  intent = "contact",
  onClose,
}: {
  leadId: number
  // Undefined when adding: the entry does not exist yet.
  person?: ContactFormPerson
  intent?: ContactDialogIntent
  onClose: () => void
}) {
  const tr = useTr(LEADS_PANE_T)
  const config = useAutoleadsConfig()
  const isEdit = person != null
  const phoneFirst = !isEdit && intent === "phone"
  const [name, setName] = useState(person?.name ?? "")
  const [role, setRole] = useState(person?.role ?? "")
  const [emails, setEmails] = useState(() =>
    toRows(person?.emails, !phoneFirst),
  )
  const [phones, setPhones] = useState(() => toRows(person?.phones, true))
  const [linkedin, setLinkedin] = useState(person?.linkedin_url ?? "")
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS)

  const addContact = useAddLeadContact({
    successMessage: tr("contactCreated"),
    onSuccess: onClose,
  })
  const updateContact = useUpdateLeadContact({
    successMessage: tr("contactSaved"),
    onSuccess: onClose,
  })
  const pending = addContact.isPending || updateContact.isPending

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const found = validate({ name, emails, phones, linkedin }, person)
    setErrors(found)
    if (hasErrors(found)) return
    const payload: LeadContactPayload = {
      name: name.trim(),
      role: role.trim(),
      emails: filledValues(emails),
      phones: filledValues(phones),
      linkedin_url: linkedin.trim(),
    }
    if (person?.index != null) {
      updateContact.mutate({ lead_id: leadId, index: person.index, ...payload })
      return
    }
    addContact.mutate({ lead_id: leadId, ...payload })
  }

  const title = isEdit
    ? tr("contactEdit")
    : phoneFirst
      ? tr("contactAddPhone")
      : tr("contactAdd")
  const description = isEdit
    ? tr("contactEditDescription")
    : phoneFirst
      ? tr("contactPhoneDescription")
      : tr("contactAddDescription")

  // Both lists share one remove column as soon as either needs it, so the
  // email and phone inputs stay aligned.
  const listSlot = emails.length > 1 || phones.length > 1
  const phoneList = (
    <ValueList
      legend={tr("contactPhones")}
      rows={phones}
      errors={errors.phones}
      addLabel={tr("contactAddPhone")}
      autoFocusFirst={phoneFirst}
      reserveSlot={listSlot}
      onChange={(rows) => {
        setPhones(rows)
        setErrors((current) => ({ ...current, phones: {}, form: undefined }))
      }}
      renderInput={(row, inputId, setValue) =>
        fitsPhoneInput(row.value) ? (
          <PhoneNumberInput
            id={inputId}
            config={config}
            value={row.value}
            onNumberChange={setValue}
            disabled={pending}
          />
        ) : (
          <Input
            id={inputId}
            type="tel"
            inputMode="tel"
            value={row.value}
            disabled={pending}
            onChange={(event) => setValue(event.target.value)}
          />
        )
      }
    />
  )

  const identity = (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field>
        <FieldLabel htmlFor="contact-name">
          {phoneFirst ? tr("contactPhoneLabel") : tr("contactName")}
        </FieldLabel>
        <Input
          id="contact-name"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="off"
          autoFocus={!phoneFirst}
          disabled={pending}
          onChange={(event) => {
            setName(event.target.value)
            setErrors((current) => ({ ...current, form: undefined }))
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="contact-role">{tr("contactRole")}</FieldLabel>
        <Input
          id="contact-role"
          value={role}
          maxLength={ROLE_MAX_LENGTH}
          autoComplete="off"
          disabled={pending}
          onChange={(event) => setRole(event.target.value)}
        />
      </Field>
    </div>
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg md:max-w-lg lg:max-w-lg xl:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate>
          <FieldGroup className="gap-5">
            {phoneFirst ? (
              <>
                {phoneList}
                {identity}
              </>
            ) : (
              <>
                {identity}
                <FieldSeparator />
                <ValueList
                  legend={tr("contactEmails")}
                  rows={emails}
                  errors={errors.emails}
                  addLabel={tr("contactAddEmail")}
                  reserveSlot={listSlot}
                  onChange={(rows) => {
                    setEmails(rows)
                    setErrors((current) => ({
                      ...current,
                      emails: {},
                      form: undefined,
                    }))
                  }}
                  renderInput={(row, inputId, setValue) => (
                    <Input
                      id={inputId}
                      type="email"
                      inputMode="email"
                      autoComplete="off"
                      value={row.value}
                      disabled={pending}
                      aria-invalid={Boolean(errors.emails[row.id])}
                      onChange={(event) => setValue(event.target.value)}
                    />
                  )}
                />
                {phoneList}
                <Field data-invalid={Boolean(errors.linkedin)}>
                  <FieldLabel htmlFor="contact-linkedin">
                    {tr("contactLinkedin")}
                  </FieldLabel>
                  <Input
                    id="contact-linkedin"
                    type="url"
                    inputMode="url"
                    placeholder={tr("contactLinkedinPlaceholder")}
                    value={linkedin}
                    maxLength={LINKEDIN_MAX_LENGTH}
                    disabled={pending}
                    aria-invalid={Boolean(errors.linkedin)}
                    onChange={(event) => {
                      setLinkedin(event.target.value)
                      setErrors((current) => ({
                        ...current,
                        linkedin: undefined,
                      }))
                    }}
                  />
                  {errors.linkedin && (
                    <FieldError>{tr(errors.linkedin)}</FieldError>
                  )}
                </Field>
              </>
            )}
            {errors.form && <FieldError>{tr(errors.form)}</FieldError>}
          </FieldGroup>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              {tr("contactCancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="size-4 animate-spin" />}
              {isEdit ? tr("contactSave") : tr("contactAddShort")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
